import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const HostelAttendance = ({ onNavigate, userInfo }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState([]);
  const [stats, setStats] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // User privileges state
  const [userPrivileges, setUserPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
      fetchUserPrivileges();
      fetchAttendance();
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo?.userId) {
      const timer = setTimeout(() => {
        fetchAttendance();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedDate, searchQuery]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  // Fetch user privileges
  const fetchUserPrivileges = async () => {
    try {
      setPrivilegesLoading(true);
      const response = await fetch(`${API_URL}/api/admin/user-privileges/${userInfo.userId}`);
      const data = await response.json();
      
      if (data.success && data.privileges) {
        setUserPrivileges(data.privileges);
      } else {
        console.error('Failed to load privileges:', data.message);
        setUserPrivileges([]);
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
      setUserPrivileges([]);
    } finally {
      setPrivilegesLoading(false);
    }
  };

  // Group privileges by category
  const getPrivilegesByCategory = (category) => {
    return userPrivileges.filter(privilege => privilege.page_category === category);
  };

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (selectedDate) queryParams.append('date', selectedDate);
      if (searchQuery) queryParams.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/admin/attendance/${userInfo.userId}?${queryParams}`);
      const data = await response.json();
      
      if (data.success) {
        setAttendanceData(data.attendanceData || []);
        setStats(data.stats || {
          totalCheckIns: 0,
          totalCheckOuts: 0,
          currentlyInside: 0,
          currentlyOutside: 0
        });
      } else {
        setError(data.message || 'Failed to load attendance');
        setAttendanceData([]);
      }
    } catch (err) {
      console.error('Error fetching attendance:', err);
      setError('Failed to connect to server');
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const MenuItem = ({ emoji, label, active = false, route }) => (
    <div 
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setMobileMenuOpen(false);
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const filteredAttendance = attendanceData.filter(record => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      record.studentName?.toLowerCase().includes(query) ||
      record.studentId?.toLowerCase().includes(query) ||
      record.room?.toString().includes(query)
    );
  });

  if (loading && attendanceData.length === 0 && !stats) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f2f8f7',
            borderTop: '4px solid #2b6777',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#2b6777',
            fontSize: '16px',
            fontWeight: '600',
            margin: 0
          }}>Loading Attendance...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%);
          overflow-x: hidden;
        }

        .dashboard-container {
          min-height: 100vh;
          display: flex;
          position: relative;
        }

        /* FIXED SIDEBAR - Matching Dashboard */
        .sidebar {
          position: fixed;
          left: 0;
          top: 0;
          width: 280px;
          height: 100vh;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: 4px 0 24px rgba(43,103,119,0.1);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          border-right: 2px solid rgba(82,171,152,0.15);
          transition: transform 0.3s ease;
        }

        /* Mobile overlay */
        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 999;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s;
        }

        .mobile-overlay.show {
          opacity: 1;
          pointer-events: all;
        }

        .sidebar-header {
          padding: 28px 22px;
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.6) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
        }

        .sidebar-top {
          display: none;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .sidebar-close-btn {
          background: rgba(43,103,119,0.08);
          border: 1.5px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .sidebar-close-btn:hover {
          background: rgba(43,103,119,0.15);
          border-color: #52ab98;
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 14px;
          position: relative;
          z-index: 1;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
          color: white;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          border: 3px solid rgba(255, 255, 255, 0.5);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          padding: 28px 20px;
          overflow-y: auto;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 32px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: rgba(43,103,119,0.5);
          margin-bottom: 12px;
          padding-left: 18px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          opacity: 1;
          animation: glow 2s ease-in-out infinite;
        }

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 20px;
          border-top: 2px solid rgba(82,171,152,0.15);
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .logout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(43,103,119,0.4);
        }

        /* MAIN CONTENT - Matching Dashboard */
        .main-content {
          flex: 1;
          margin-left: 280px;
          padding: 20px;
          width: calc(100% - 280px);
        }

        .top-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 18px 24px;
          border-radius: 18px;
          box-shadow: 0 6px 24px rgba(43,103,119,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          border: 2px solid rgba(82,171,152,0.12);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .mobile-menu-toggle {
          display: none;
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.2);
          cursor: pointer;
          font-size: 22px;
          color: #2b6777;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .mobile-menu-toggle:hover {
          background: linear-gradient(135deg, rgba(43,103,119,0.2) 0%, rgba(82,171,152,0.2) 100%);
          border-color: #52ab98;
          transform: scale(1.05);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .header-name {
          font-size: 20px;
          font-weight: 800;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 16px;
          color: white;
          box-shadow: 0 6px 20px rgba(43,103,119,0.25);
          border: 3px solid rgba(255, 255, 255, 0.8);
        }

        /* Page Title Section */
        .page-title-section {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 24px;
          padding: 40px 36px;
          margin-bottom: 28px;
          box-shadow: 0 12px 48px rgba(43,103,119,0.3);
          position: relative;
          overflow: hidden;
        }

        .page-title-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          filter: blur(60px);
        }

        .page-title-content {
          position: relative;
          z-index: 1;
        }

        .page-title {
          font-size: 28px;
          font-weight: 900;
          color: white;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        /* Stats Cards */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          transition: all 0.3s;
          border: 2px solid rgba(82,171,152,0.12);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.25);
        }

        .stat-card:hover::before {
          opacity: 1;
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 18px;
        }

        .stat-icon {
          width: 54px;
          height: 54px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 6px 20px rgba(43,103,119,0.15);
          transition: all 0.3s;
        }

        .stat-card:hover .stat-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .stat-body {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
          letter-spacing: -1px;
        }

        /* Filters Section */
        .filters-section {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
        }

        .filters-grid {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 16px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-label {
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
        }

        .filter-input {
          padding: 12px 16px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.3s;
          background: white;
        }

        .filter-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        /* Attendance Table */
        .attendance-table-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
        }

        .table-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .table-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .table-count {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          color: #2b6777;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          border: 2px solid rgba(82,171,152,0.2);
        }

        .attendance-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0 12px;
        }

        .attendance-table thead th {
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.6) 100%);
          padding: 16px;
          text-align: left;
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: none;
        }

        .attendance-table thead th:first-child {
          border-radius: 12px 0 0 12px;
        }

        .attendance-table thead th:last-child {
          border-radius: 0 12px 12px 0;
        }

        .attendance-table tbody tr {
          background: rgba(242,248,247,0.5);
          transition: all 0.3s;
          border-radius: 12px;
        }

        .attendance-table tbody tr:hover {
          background: rgba(242,248,247,0.8);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .attendance-table tbody td {
          padding: 18px 16px;
          font-size: 14px;
          color: #2b6777;
          border: none;
        }

        .attendance-table tbody td:first-child {
          border-radius: 12px 0 0 12px;
        }

        .attendance-table tbody td:last-child {
          border-radius: 0 12px 12px 0;
        }

        .student-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .student-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          color: white;
        }

        .student-details {
          display: flex;
          flex-direction: column;
        }

        .student-name {
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 2px;
        }

        .student-id {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 700;
        }

        .status-badge.check-in {
          background: rgba(46, 213, 115, 0.15);
          color: #2ed573;
        }

        .status-badge.check-out {
          background: rgba(255, 177, 66, 0.15);
          color: #ffb142;
        }

        .no-data-message {
          text-align: center;
          padding: 60px 20px;
          color: rgba(43,103,119,0.5);
        }

        .no-data-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .no-data-text {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .no-data-subtext {
          font-size: 14px;
        }

        .no-privileges-message {
          padding: 40px;
          text-align: center;
          background: rgba(255,249,196,0.3);
          border-radius: 16px;
          border: 2px dashed rgba(255,193,7,0.4);
          margin: 20px;
        }

        .no-privileges-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .no-privileges-text {
          font-size: 16px;
          color: #2b6777;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .no-privileges-subtext {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
        }

        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }

          .mobile-overlay {
            display: block;
          }

          .sidebar-top {
            display: flex;
          }

          .mobile-menu-toggle {
            display: flex;
          }

          .main-content {
            margin-left: 0;
            width: 100%;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .filters-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .page-title {
            font-size: 24px;
          }

          .attendance-table-container {
            overflow-x: auto;
          }

          .attendance-table {
            min-width: 600px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Mobile Overlay */}
        <div 
          className={`mobile-overlay ${mobileMenuOpen ? 'show' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* FIXED SIDEBAR - Same as Dashboard */}
        <div className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setMobileMenuOpen(false)} className="sidebar-close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{dashboardData?.adminName || 'Admin User'}</div>
                <div className="user-role-sidebar">
                  {dashboardData?.hostelName || 'Hostel Administrator'}
                </div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            {/* Dynamically render menu sections based on user privileges */}
            
            {/* Overview Section */}
            {getPrivilegesByCategory('overview').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Overview</div>
                {getPrivilegesByCategory('overview').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    active={privilege.page_route === 'hostel-admin-dashboard'} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Management Section */}
            {getPrivilegesByCategory('management').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Management</div>
                {getPrivilegesByCategory('management').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    active={privilege.page_route === 'hostel-admin-attendance'}
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Approval Section */}
            {getPrivilegesByCategory('approval').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Approval</div>
                {getPrivilegesByCategory('approval').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Settings Section */}
            {getPrivilegesByCategory('settings').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Settings</div>
                {getPrivilegesByCategory('settings').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Reports Section */}
            {getPrivilegesByCategory('reports').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Reports</div>
                {getPrivilegesByCategory('reports').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Show message if no privileges */}
            {userPrivileges.length === 0 && !privilegesLoading && (
              <div className="no-privileges-message">
                <div className="no-privileges-icon">🔒</div>
                <div className="no-privileges-text">No Access Granted</div>
                <div className="no-privileges-subtext">Please contact administrator</div>
              </div>
            )}
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button 
                className="mobile-menu-toggle" 
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Toggle Menu"
              >
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Check In Management</div>
                <div className="header-name">{dashboardData?.adminName || 'Admin'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>

          {/* Page Title */}
          <div className="page-title-section">
            <div className="page-title-content">
              <div className="page-title">Student Check-In Records</div>
              <div className="page-subtitle">Monitor and track student entry/exit activities</div>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(46,213,115,0.15) 0%, rgba(46,213,115,0.25) 100%)' }}>
                    ✅
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Check-Ins Today</div>
                  <div className="stat-value">{stats.totalCheckIns || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(255,177,66,0.15) 0%, rgba(255,177,66,0.25) 100%)' }}>
                    🚪
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Check-Outs Today</div>
                  <div className="stat-value">{stats.totalCheckOuts || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(52,172,224,0.15) 0%, rgba(52,172,224,0.25) 100%)' }}>
                    🏠
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Currently Inside</div>
                  <div className="stat-value">{stats.currentlyInside || 0}</div>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-header">
                  <div className="stat-icon" style={{ background: 'linear-gradient(135deg, rgba(149,175,192,0.15) 0%, rgba(149,175,192,0.25) 100%)' }}>
                    🌍
                  </div>
                </div>
                <div className="stat-body">
                  <div className="stat-label">Currently Outside</div>
                  <div className="stat-value">{stats.currentlyOutside || 0}</div>
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="filters-section">
            <div className="filters-grid">
              <div className="filter-group">
                <label className="filter-label">Select Date</label>
                <input
                  type="date"
                  className="filter-input"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">Search Student</label>
                <input
                  type="text"
                  className="filter-input"
                  placeholder="Search by name, ID, or room number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Attendance Table */}
          <div className="attendance-table-container">
            <div className="table-header">
              <div className="table-title">Attendance Records</div>
              <div className="table-count">{filteredAttendance.length} Records</div>
            </div>

            {filteredAttendance.length > 0 ? (
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Room</th>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Gate</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendance.map((record, index) => (
                    <tr key={index}>
                      <td>
                        <div className="student-info">
                          <div className="student-avatar">
                            {record.studentName ? record.studentName.charAt(0).toUpperCase() : 'S'}
                          </div>
                          <div className="student-details">
                            <div className="student-name">{record.studentName || 'Unknown'}</div>
                            <div className="student-id">{record.studentId || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td>{record.room || 'N/A'}</td>
                      <td>{record.time || 'N/A'}</td>
                      <td>
                        <span className={`status-badge ${record.type === 'entry' ? 'check-in' : 'check-out'}`}>
                          {record.type === 'entry' ? '✅ Check-In' : '🚪 Check-Out'}
                        </span>
                      </td>
                      <td>{record.gate || 'Main Gate'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data-message">
                <div className="no-data-icon">📋</div>
                <div className="no-data-text">No Attendance Records Found</div>
                <div className="no-data-subtext">
                  {searchQuery ? 'Try adjusting your search' : 'No records for selected date'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HostelAttendance;